import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Strings from '../../../../i18n';
import { ErrorText } from '../../../../components/auth/Profile/completeProfileStyles';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarEditablePropertyItem } from '../../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { createSerialNumberSideBarProps } from './createSerialNumberSideBarProps';
import { serialNumberValidation } from '../../../../utils/validators';
import {
  PartNumberData,
  useCreateSerialNumberMutation,
  useGetPartNumberMutation
} from '../../../../services/manufacture/manufactureService';
import { MultiSelectWithChipsAndSendButtonSideBar } from '../../../../components/commons/SideBarComponents/MultiSelectWithChipsAndSendButton/multiSelectChipsSenSideBar';

export const CreateSerialNumberSideBar = ({
  reloadTable,
  setOpenSideBar
}: createSerialNumberSideBarProps) => {
  const [data, setData] = useState<PartNumberData[]>([]);
  const [filteredData, setFilteredData] = useState<PartNumberData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedPartNumber, setSelectedPartNumber] =
    useState<PartNumberData | null>(null);

  const [createSerialNumber, { isSuccess, isError }] =
    useCreateSerialNumberMutation();

  const [
    getPartNumber,
    { isSuccess: isSuccessPartNumber, data: partNumberData }
  ] = useGetPartNumberMutation();

  useEffect(() => {
    getPartNumber(null);
  }, []);

  useEffect(() => {
    if (isSuccessPartNumber && partNumberData) {
      setData(partNumberData);
      setFilteredData(partNumberData);
    }
  }, [isSuccessPartNumber, partNumberData]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Data was saved successfully.');
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      toast.error('Something went wrong, try againg later.');
    }
  }, [isError]);

  return (
    <Formik
      enableReinitialize
      validationSchema={serialNumberValidation}
      initialValues={{
        partNumber: '',
        quantity: 1,
        soNumber: ''
      }}
      onSubmit={(values) => {
        if (!selectedPartNumber) {
          toast.error('Please, select a Part Number to continue');
          return;
        }
        createSerialNumber({
          partNumber: selectedPartNumber.partNumber,
          quantity: values.quantity
        });
      }}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        setFieldTouched,
        errors,
        touched
      }) => (
        <>
          <StyledContainerItems>
            <StyledTitleSideBar>
              {Strings.manufacture.serialNumber.title}
            </StyledTitleSideBar>
            {isSuccessPartNumber && data && (
              <>
                <SideBarEditablePropertyItem
                  item={{
                    label: Strings.manufacture.partNumber.create.partNumber,
                    type: 'inputText',
                    currentValue: searchText,
                    nameAndId: 'partNumber',
                    onChange: (e) => {
                      setSearchText(e.target.value);
                      const filtered = data.filter((item) =>
                        `${item.product} ${item.series} ${item.model} ${item.partNumber}`
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase())
                      );
                      setFilteredData(filtered);
                    }
                  }}
                />
                {searchText.trim() && filteredData.length > 0 && (
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginBottom: '10px'
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Part Number</th>
                        <th>Product</th>
                        <th>Series</th>
                        <th>Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item) => (
                        <tr
                          key={item.id}
                          style={{
                            cursor: 'pointer',
                            backgroundColor:
                              selectedPartNumber?.id === item.id
                                ? '#eef'
                                : 'transparent'
                          }}
                          onClick={() => setSelectedPartNumber(item)}
                        >
                          <td>{item.partNumber}</td>
                          <td>{item.product}</td>
                          <td>{item.series}</td>
                          <td>{item.model}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {searchText.trim() &&
                  filteredData.length > 0 &&
                  selectedPartNumber && (
                    <div style={{ marginBottom: '10px', fontStyle: 'italic' }}>
                      <strong>Description: </strong>
                      {selectedPartNumber.description}
                    </div>
                  )}
              </>
            )}
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.serialNumber.create.quantity,
                type: 'inputNumber',
                currentValue: values.quantity,
                nameAndId: 'quantity',
                onChange: handleChange('quantity'),
                onBlur: () => setFieldTouched('quantity')
              }}
            />
            {touched.quantity && errors.quantity && (
              <ErrorText>{String(errors.quantity)}</ErrorText>
            )}
          </StyledContainerItems>
          <StyledContainerFooterSideBar>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setOpenSideBar(false);
                setSelectedPartNumber(null);
              }}
            >
              {Strings.common.cancel}
            </button>
            <Button
              variant="contained"
              size="large"
              className="btn-save"
              onClick={() => handleSubmit()}
            >
              {Strings.common.save}
            </Button>
          </StyledContainerFooterSideBar>
        </>
      )}
    </Formik>
  );
};
